import React from 'react';

import { Body, Caption } from '@depop/web-thrift-ui/Typography';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';

import styles from './styles.module.css';

import { BagDeleteProductButton } from '@/components/Bag/BagDeleteProductButton';
import { ExtendedLink } from '@/components/ExtendedLink';
import { InBagsNudge } from '@/components/InBagNudge';
import { LocaleCurrencyFormat } from '@/components/LocaleCurrencyFormat';
import { Price } from '@/components/Price';
import { RQ_X_COUNTRY_CODE } from '@/modules/ReactQuery/cacheKeys';
import { type DetailedBagProduct } from '@/modules/bag/aliases';
import { getEstimatedShippingPrice } from '@/modules/bag/helpers';
import { type CurrencyCode } from '@/modules/currencies/constants';
import { ON_IDENTIFIER } from '@/modules/experiments/config';
import { useExperiments } from '@/modules/experiments/useExperiments';
import { type ProductNudge } from '@/modules/nudges/aliases';
import { getNudgeByType } from '@/modules/nudges/helpers/getNudgeByType';
import {
  filterRequiredPictures,
  getNearestSizePictureFormat,
} from '@/modules/pictures/helpers';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

type Props = {
  product: DetailedBagProduct;
  sellerId: number;
  className?: string;
  showEstimatedShipping?: boolean;
  showInBagNudge?: boolean;
  nudges: ProductNudge[];
};

function BagProduct({
  product,
  sellerId,
  className = '',
  showEstimatedShipping = false,
  nudges,
}: Props) {
  const queryClient = useQueryClient();
  const [, currentUser] = useCurrentUser();
  const buyerCountry = currentUser
    ? currentUser.country
    : queryClient.getQueryData<string>([RQ_X_COUNTRY_CODE]);
  const t = useTranslations('bag');
  const estimatedShippingCost = getEstimatedShippingPrice({
    sellerLocation: product.country,
    buyerLocation: buyerCountry,
    nationalShippingCost: product.pricing.national_shipping_cost?.total_price,
    internationalShippingCost:
      product.pricing.international_shipping_cost?.total_price,
  });

  const { web_upfront_shipping_banner } = useExperiments([
    'web_upfront_shipping_banner',
  ]);
  const bannerIsDisplayed = web_upfront_shipping_banner === ON_IDENTIFIER;

  const inBagsNudge = getNudgeByType(nudges, 'bag');

  // @TODO this fixes an issue where a video only product ends up with a broken image
  // for a tumbnail. Longer term we should return the video array for the product
  // and then we can grab the video thumbnail if there are no images.
  // This only came up because mobile are getting ready to roll out the bag. It's not
  // possible to have a video only product on the web.
  const imgUrl = getNearestSizePictureFormat(
    filterRequiredPictures(product.pictures?.[0] || []),
    120
  )?.url;
  const displayShippingCost =
    !bannerIsDisplayed && showEstimatedShipping && estimatedShippingCost;
  return (
    <div className={`${className} ${styles.container}`}>
      {imgUrl && (
        <ExtendedLink href={`/products/${product.slug}`}>
          <img
            className={styles.productImage}
            src={imgUrl}
            alt={product.description}
          />
        </ExtendedLink>
      )}
      {!imgUrl && (
        <ExtendedLink href={`/products/${product.slug}`}>
          <div
            title={product.description}
            className={`${styles.productImage} ${styles.productPlaceholder}`}
          />
        </ExtendedLink>
      )}
      <div className={styles.productDetails}>
        <InBagsNudge
          className={styles.nudges}
          count={inBagsNudge?.data?.count}
        />
        <Body variant="sm-regular" className={styles.description}>
          {product.description}
        </Body>
        <Price
          className={styles.text}
          inPreview
          /**
           * @TECHDEBT - PB 16.6.25 - https://depopmarket.atlassian.net/browse/WEB-7154
           * CurrencyCode backend type needs updating to a string union to avoid this type cast
           */
          currencyCode={product.pricing.currency as CurrencyCode}
          price={product.pricing.original_price.total_price}
          discountedPrice={
            product.pricing.is_reduced
              ? product.pricing[product.pricing.final_price_key]?.total_price
              : undefined
          }
        />
        {displayShippingCost && (
          <Body variant="sm-regular" className={styles.shippingPriceCaption}>
            {t.rich('Bag.QuickAccess.Shipping', {
              localeCurrency: () => (
                <LocaleCurrencyFormat
                  /**
                   * @TECHDEBT - PB 16.6.25 - https://depopmarket.atlassian.net/browse/WEB-7154
                   * CurrencyCode backend type needs updating to a string union to avoid this type cast
                   */
                  currencyCode={product.pricing.currency as CurrencyCode}
                  numberToFormat={estimatedShippingCost}
                />
              ),
            })}
          </Body>
        )}
        {product.size && (
          <Caption variant="lg-regular" className={styles.text}>
            {product.size}
          </Caption>
        )}
        <BagDeleteProductButton product={product} sellerId={sellerId} />
      </div>
    </div>
  );
}

export { BagProduct };
